import React from "react";
import { DialogTitle } from "~/components/dialog";
import CurrencyFormField from "~/visuals/organisms/CurrencyFormField";
import FormBottomRow from "~/components/form-bottom-row";
import { ErrorRenderer, MutationForm } from "~/components/forms/MutationForm";
import { ValidationResponse } from "~/gql/types";
import { ProductDialog, FieldNote } from "./dialogChrome";
import { Product, unitNoun } from "./shared";

type AddRateState = {
  amount: string;
};

export type AddProductRateDialogProps = {
  product: Product;
  open: boolean;
  loading: boolean;
  onCancel: () => void;
  onAddRate: (amount: number) => Promise<ValidationResponse>;
  onSuccess: () => void;
};

export const AddProductRateDialog: React.FC<AddProductRateDialogProps> = ({
  product,
  open,
  loading,
  onCancel,
  onAddRate,
  onSuccess,
}) => {
  const unitLabel = unitNoun[product.unit];

  const initialValues: AddRateState = { amount: "" };

  return (
    <ProductDialog open={open} onClose={onCancel}>
      <DialogTitle>
        Add Rate — {product.name} ({product.unit})
      </DialogTitle>
      <MutationForm
        initialValues={initialValues}
        runMutation={(values: AddRateState) => onAddRate(Number(values.amount))}
        onSuccess={onSuccess}
      >
        {/* A currency field, not a plain text one: its `required` check also
            rejects anything `Number()` cannot read, so the form blocks submit
            rather than letting `"abc"` through as NaN (which serializes to
            null, and would be reported back as a *missing* amount) or `"12abc"`
            through as a silently truncated 12. */}
        <CurrencyFormField
          formField="amount"
          label={`Amount per ${unitLabel}`}
          helperText={`The catalogued price for one ${unitLabel} (0 is valid)`}
          required
        />
        <FieldNote>
          This product is billed by the {unitLabel}. The amount you enter is the
          price for a single {unitLabel}.
        </FieldNote>

        <ErrorRenderer
          render={(error) => (
            <FormBottomRow
              buttonText="Add"
              loading={loading}
              onCancel={onCancel}
              errorMessage={error}
            />
          )}
        />
      </MutationForm>
    </ProductDialog>
  );
};
